<script lang="ts" setup>
import { reactive, computed, onMounted, watch, inject, ref } from 'vue';
import Chart, { IChartdata } from '@/components/atom/Chart.vue';
import { newDefaultData } from '@/appLogic/importData/initialData';
import { calculateDataToDays, dateObjToUnixTime, dateStrToObject } from '@/appLogic/business/businessLogic';
import { simpleCopy } from '@/utils/deepCopy';
import { changeMonthChart, changeDayChart } from '@/appLogic/view/orderChart';
import {
  FORECAST_LENGTH,
  YEAR_TO_MONTH,
  DAY_NUM_OF_BIZDAYS,
  MONTH_NUM_OF_BIZDAYS,
  DAY_STD,
  UNIT_OF_DAY,
  UNIT_OF_WEEK,
  UNIT_OF_MONTH,
} from '@/coreDomainsModule/constants';
import { DateService } from '@/coreDomainsModule/services/dateService';
import calculateRecommendOrderQuantity from '@/appLogic/business/calculateRecommendOrderQuantity';
import {
  stdIndexInfosToStdInventoryIndexObject,
  stdInventoryIndexObjectToStdIndexInfos,
  stdInventoryIndexToDisplayStdIndexInfo,
} from '@/appLogic/business/stdInventoryIndex';
import { i18n } from '@/i18n';
import SelectableSimpleTableContent from './SelectableSimpleTableContent.vue';
import { useRouter } from 'vue-router';
import { objectEquals } from '@/utils/objectEquals';
import TableCell from '@/components/atom/TableCell.vue';
import TableHead from '@/components/atom/TableHead.vue';
import LeadTimePicker from '@/components/modal/LeadTimePicker.vue';
import StdInventoryPicker from '@/components/modal/StdInventoryPicker.vue';
import { FloatPopupState } from '@/appLogic/interfaces/floatPopup';
import WorkDaysCalendarNotYetEnteredAlertModal from '@/components/modal/WorkDaysCalendarNotYetEnteredAlertModal.vue';
import { shouldShowWorkDaysCalendarNotEnteredAlertModal } from '@/appLogic/importData/calendar';
import Modal from '@/components/modal/Modal.vue';
import { IExtraSimulate } from '@/recommendOrderModule/pages/interfaces/extraSimulation';
import { IFutureMonthlyCalculationsRow } from '../pages/interfaces/recommendOrder';
import { loadMyPropertyPreferences, myPropertyPreferences, saveMyPropertyPreferences } from '@/appLogic/view/preferences';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { nullOrUndefinedToMinusOne } from '@/coreDomainsModule/extends/number';
import { IYearMonth } from '@/coreDomainsModule/date';
import { CreateChartDataForCsvUseCase } from '@/exportFileUseCasesModule/createChartDataForCsvUseCase';
import { CreateCsvInfo } from '@/coreDomainsModule/exportFileManage';
import { openFailToSavedModal } from '@/corePagesModule/components/modal/modalActions';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IMvpInventory, StdIndexInfo } from '@/coreDomainsModule/entities/inventory';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { FixOrderValue, ISelectFormDataSet, ISelectOption, OrderChartDateUnit, TableType } from '@/coreDomainsModule/table';
import { IDate } from '@/coreDomainsModule/date';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { useSelectedBusinessStore } from '../pages/selectedBusinessStore';
import { FORECAST_UPPER_LIMIT, EQUAL_RATE } from '@/recommendOrderModule/constants';
import SvgLoader from '@/utils/SvgLoader.vue';
import { key } from '../pages/useRecommendOrderStore';
import InvalidOrderAlert from './InvalidOrderAlert.vue';
import { AnnounceService } from '../domains/services/announceService';
import MoqInputPopUp from './MoqInputPopUp.vue';
import OrderPackageInputPopUp from './OrderPackageInputPopUp.vue';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import OptimizedTooltipText from '@/corePagesModule/components/OptimizedTooltipText.vue';
import RelatedTransactionsModal from './RelatedTransactionsModal.vue';
import SimpleSelectInput from '@/corePagesModule/components/SimpleSelectInput/SimpleSelectInput.vue';
/**
 *
 */
interface IOrderForm {
  formSet: IBusiness;
  chartData: IChartdata;
  timeUnits: ISelectOption[];
  selectedDateUnit: OrderChartDateUnit;
  calculationUnit: ISelectOption[];
  selectedCalculationUnit: string;
  orderAmount: number;
  deliveryDate: IDate;
  leadTimeSet: ISelectFormDataSet;
  inventoryIndexSet: ISelectFormDataSet;
  moqModelValue: number;
  orderPackageModelValue: number; // 発注単位のモデル値
  stdIndexInfos: StdIndexInfo[];
  displayStdIndexInfo: StdIndexInfo;
  options: ISelectOption[];
  forecastDates: ForecastInfo[]; // 当月入力の内示
  forecastHistory: ForecastInfo[]; // 過去の内示実績
  turnOverHistory: TurnOverInfo[]; // 過去の在庫回転率実績
  inventoryHistory: IMvpInventory[]; // 過去の末在庫履歴
  forecastDatesStack: ForecastInfo[][]; // `forecastDates`の変更履歴スタック(とりあえず計算した場合のみ記録)
  currentForecastIndex: number;
  currentMonthInputSalesResult: ISalesResult[];
  rate: number;
  operator: string;
  isActiveForecastButtons: boolean;
  inputGraphRange: number[];
  differenceFromStartDate: number; // シミュレーション開始日からの差分（開始日を0とする）
  graphRange: number; // グラフの表示範囲(>0)
  isFirstRender: boolean;
  isRenderForecastHistory: boolean;
  quantityRate: number[];
  leadTimePickerState: FloatPopupState; // リードタイムピッカーの表示状態
  stdInventoryPickerState: FloatPopupState; // 標準在庫指数ピッカーの表示状態
  moqInputPopUpState: FloatPopupState; // MOQインプットの表示状態
  orderPackageInputPopUpState: FloatPopupState; // 発注単位インプットの表示状態
  workDaysCalendarNotYetEnteredAlertModalStatus: boolean;
  historyMonthOffset: number;
  formSetCustomerForecastHistory: IAnnounce[]; // DBから受け取った内示情報の履歴情報を入れる用
  isAppearenceUnpaidOrderedOrder: boolean;
  futureForecasts: ForecastInfo[];
  currentMonthData: { salesResult: ISalesResult; quantityRate: number } | undefined;
}

/**
 *  自身の初期数量を持つ内示情報
 */
interface ForecastInfo extends IAnnounce {
  initialAmount: number;
  approvedOrdersAmount: number;
  endOfMonthInventory: number;
  inventoryTurnoverRate: number;
}

/**
 * 在庫回転率情報
 */
interface TurnOverInfo extends IYearMonth {
  turnOver: number; // 在庫回転率
}

const props = withDefaults(
  defineProps<{
    item: IBusiness;
    formSetCustomerForecastHistory: IAnnounce[];
    tableType: TableType;
    fixOrderValue?: FixOrderValue; //?なのでrequired: falseと同じ
    extraSimulateInfos?: IExtraSimulate;
  }>(),
  {
    fixOrderValue: undefined,
    extraSimulateInfos: undefined,
  }
);

const emits = defineEmits<{
  (e: 'submit'): void;
  (e: 'updateSimulate', value: IArrival[]): void;
  (e: 'cancel', isCancel: boolean): void;
  (e: 'updateBusinessId'): void;
  (e: 'pagingUpdate', aggregateBusinessId: string): void;
}>();

/**
 * 取引の発注（recommend）の中から最も遅い入荷予定日(crossDate)をDate型で返す。
 * 分納(partialDeliveries)のcrossDteも対象に含める。
 * 該当する入荷予定日が無い場合はundefinedを返す。
 * @param business - 取引情報
 */
const getLatestArrivalDate = (business: IBusiness): Date | undefined => {
  const recommends = business.recommend ?? [];
  let latest: Date | undefined;
  for (const order of recommends) {
    if (order.crossDate) {
      const d = DateService.convertIDateToDate(order.crossDate);
      if (!latest || d > latest) latest = d;
    }
    if (order.partialDeliveries) {
      for (const pd of order.partialDeliveries) {
        if (pd.crossDate) {
          const d = DateService.convertIDateToDate(pd.crossDate);
          if (!latest || d > latest) latest = d;
        }
      }
    }
  }
  return latest;
};

//変数
const t = i18n.t;
const router = useRouter();
const createChartDataForCsvUseCase = new CreateChartDataForCsvUseCase();
const recommendOrderStore = inject(key);
if (!recommendOrderStore) {
  throw new Error('store is undefined');
}
const { state: useSelectedBusinessState } = useSelectedBusinessStore();

const isExpanded = ref(false);
const state: IOrderForm = reactive<IOrderForm>({
  formSet: newDefaultData,
  selectedDateUnit: 'day',
  timeUnits: [
    { label: '稼働日ごとの在庫予想', value: 'day' },
    { label: '月ごとの在庫予想', value: 'month' },
  ],
  selectedCalculationUnit: 'multiplication',
  calculationUnit: [
    { label: '×', value: 'multiplication' },
    { label: '÷', value: 'division' },
  ],
  chartData: {
    labels: [],
    datasets: [],
  },
  orderAmount: 0,
  deliveryDate: dateStrToObject(
    new Date().getTime() + (props.item.leadTime && props.item.leadTime >= 0 ? props.item.leadTime * DAY_STD : 0)
  ),
  stdIndexInfos: [{ min: -1, max: 0, name: t('標準在庫指数(下限)(詳細ページ)'), unit: 'day' }],
  displayStdIndexInfo: { min: -1, max: 0, name: t('標準在庫指数(上限)(詳細ページ)'), unit: 'day' },
  leadTimeSet: {
    displayData: -1,
    selectedUnit: 'day',
  },
  inventoryIndexSet: {
    displayData: -1,
    selectedUnit: 'day',
  },
  moqModelValue: 0,
  orderPackageModelValue: 0,
  options: [
    { label: UNIT_OF_DAY, value: 'day' },
    { label: UNIT_OF_WEEK, value: 'week' },
    { label: UNIT_OF_MONTH, value: 'month' },
  ],
  rate: 0,
  operator: 'multiplication',
  isActiveForecastButtons: false,
  forecastDates: [],
  forecastHistory: [],
  turnOverHistory: [],
  inventoryHistory: [],
  forecastDatesStack: [],
  currentForecastIndex: 0,
  currentMonthInputSalesResult: [],
  inputGraphRange: [0, 180],
  differenceFromStartDate: 0,
  graphRange: 180,
  isFirstRender: true,
  isRenderForecastHistory: true,
  quantityRate: [],
  leadTimePickerState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  stdInventoryPickerState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  moqInputPopUpState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  orderPackageInputPopUpState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  workDaysCalendarNotYetEnteredAlertModalStatus: false,
  historyMonthOffset: 1,
  formSetCustomerForecastHistory: [],
  isAppearenceUnpaidOrderedOrder: false,
  futureForecasts: [],
  currentMonthData: undefined,
}) as IOrderForm;

/**
 * forecastDateの関数
 * @param announces - 内示情報
 * @param date - component表示時の日時情報
 */
const forecastDateFunction = (
  announces: IAnnounce[],
  date: IDate = dateStrToObject(new Date().getTime())
): { [key: string]: ForecastInfo } => {
  const map: { [key: string]: ForecastInfo } = {};
  let target = new Date(dateObjToUnixTime(date));
  let year = date.year;
  let month = date.month;
  for (let i = 0; i < FORECAST_LENGTH; i++) {
    map[`${year}-${month}`] = {
      year: year,
      month: month,
      amount: 0,
      initialAmount: 0,
      manageInitiatedYearMonth: undefined,
      createAt: undefined,
      updateAt: undefined,
    };
    target = new Date(year, month - 1, 3);
    year = target.getFullYear();
    month = target.getMonth() + 1;
  }
  for (const announce of announces || []) {
    const year = announce.year;
    const month = announce.month;
    if (map[`${year}-${month}`] !== undefined) {
      map[`${year}-${month}`].amount = announce.amount;
    }
  }
  return map;
};
/**
 * 販売実績の過去実績を作成する
 * @param salesResults - 実績情報
 * @param date - component表示時の日時情報
 */
const salesResultDateHistoryFunction = (
  salesResults: ISalesResult[],
  date: IDate = dateStrToObject(new Date().getTime())
): { [key: string]: ISalesResult } => {
  const map: { [key: string]: ISalesResult } = {};
  let target = new Date(dateObjToUnixTime(date));
  let year = date.year;
  let month = date.month;
  // 現在の月から過去12ヶ月分の年月情報を作成する
  for (let i = 0; i < YEAR_TO_MONTH; i++) {
    const oldYear = year - 1;
    map[`${oldYear}-${month}`] = { year: oldYear, month: month, amount: 0 };
    target = new Date(year, month - 1, 3);
    month++;
    if (month > 12) {
      year++;
      month = 1;
    }
  }
  for (const salesResult of salesResults || []) {
    const year = salesResult.year;
    const month = salesResult.month;
    if (map[`${year}-${month}`] !== undefined) {
      map[`${year}-${month}`].amount = salesResult.amount;
    }
  }
  return map;
};

/**
 * 在庫の過去実績を作成する
 * @param inventories - 実績情報
 * @param date - component表示時の日時情報
 */
const inventoryDateHistoryFunction = (
  inventories: IMvpInventory[],
  date: IDate = dateStrToObject(new Date().getTime())
): { [key: string]: IMvpInventory } => {
  const map: { [key: string]: IMvpInventory } = {};
  let target = new Date(dateObjToUnixTime(date));
  let year = date.year;
  let month = date.month;
  // 現在の月から過去12ヶ月分の年月情報を作成する
  for (let i = 0; i < YEAR_TO_MONTH; i++) {
    const oldYear = year - 1;
    map[`${oldYear}-${month}`] = { year: oldYear, month: month, amount: 0 };
    target = new Date(year, month - 1, 3);
    month++;
    if (month > 12) {
      year++;
      month = 1;
    }
  }
  for (const inventory of inventories || []) {
    const year = inventory.year;
    const month = inventory.month;
    if (map[`${year}-${month}`] !== undefined) {
      map[`${year}-${month}`].amount = inventory.amount;
    }
  }
  return map;
};

/**
 * 過去の内示実績12ヶ月分の情報をmapとして生成する
 * @param announces - 内示情報
 * @param date - component表示時の日時情報
 */
const forecastDateHistoryFunction = (
  announces: IAnnounce[],
  date: IDate = dateStrToObject(new Date().getTime())
): { [key: string]: ForecastInfo } => {
  const map: { [key: string]: ForecastInfo } = {};
  let target = new Date(dateObjToUnixTime(date));
  let year = date.year;
  let month = date.month;

  // 現在の月から過去12ヶ月分の年月情報を作成する
  for (let i = 0; i < YEAR_TO_MONTH; i++) {
    const oldYear = year - 1;
    map[`${oldYear}-${month}`] = {
      year: oldYear,
      month: month,
      amount: 0,
      initialAmount: 0,
      manageInitiatedYearMonth: undefined,
      createAt: undefined,
      updateAt: undefined,
    };
    target = new Date(year, month - 1, 3);
    month++;
    if (month > 12) {
      year++;
      month = 1;
    }
  }

  // 内示情報を年月で昇順、受領年月で降順にソートする
  const announceHistories: IAnnounce[] = AnnounceService.sortAnnounceHistories(simpleCopy(announces));

  const filteredAnnounceHistories: IAnnounce[] = announceHistories.filter((ent) => {
    const thresholdInitiatedDate = DateService.updateYearMonth(ent.year, ent.month, -state.historyMonthOffset);
    return (
      ent.manageInitiatedYearMonth &&
      ent.manageInitiatedYearMonth.month <= thresholdInitiatedDate.updateMonth &&
      ent.manageInitiatedYearMonth.year <= thresholdInitiatedDate.updateYear
    );
  });

  // 欲しい情報をフィルター済みの内示情報から取得する
  // NOTE: ソート&フィルター済みの内示情報配列から、指定した対象年月の内示情報が1個欲しいのでfindを使っている (見つけた先頭の情報が欲しい)
  for (const mapKey of Object.keys(map)) {
    const year = map[mapKey].year;
    const month = map[mapKey].month;
    // ソート&フィルター済みの内示情報配列から、指定した対象年月の先頭データを取得する
    map[mapKey].amount = filteredAnnounceHistories.find((ent) => ent.year === year && ent.month === month)?.amount ?? 0;
  }
  return map;
};

/**
 * 未来の内示24ヶ月分の情報をmapとして生成する
 * @param announces - 内示情報
 */
const calculateFutureForecasts = (announces: IAnnounce[]): ForecastInfo[] => {
  // ステップ1: 未来24ヶ月分の列を用意する
  const futureMonthsScaffold: ForecastInfo[] = [];
  const today = new Date();
  for (let i = 0; i < 24; i++) {
    const targetDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
    futureMonthsScaffold.push({
      year: targetDate.getFullYear(),
      month: targetDate.getMonth() + 1,
      amount: 0,
      approvedOrdersAmount: 0,
      endOfMonthInventory: 0,
      inventoryTurnoverRate: 0,
      initialAmount: 0,
      manageInitiatedYearMonth: undefined,
      createAt: undefined,
      updateAt: undefined,
    });
  }

  // ステップ2: 「中身」となる内示データを計算する
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  // 2-a. 未来の内示レコードだけを抽出
  const futureAnnounces = (announces || []).filter((announce) => {
    return announce.year > currentYear || (announce.year === currentYear && announce.month >= currentMonth);
  });

  // 2-b. 「nヶ月前」の閾値で絞り込み
  const thresholdDate = new Date();
  thresholdDate.setMonth(thresholdDate.getMonth() - state.historyMonthOffset);
  const thresholdYear = thresholdDate.getFullYear();
  const thresholdMonth = thresholdDate.getMonth() + 1;

  const filteredAnnounces = futureAnnounces.filter((announce) => {
    if (!announce.manageInitiatedYearMonth) return false;
    const receivedYear = announce.manageInitiatedYearMonth.year;
    const receivedMonth = announce.manageInitiatedYearMonth.month;
    return receivedYear < thresholdYear || (receivedYear === thresholdYear && receivedMonth <= thresholdMonth);
  });

  // 2-c. 各未来月ごとにnヶ月以上前で最新の内示を選択
  const latestForecastsMap = new Map<string, IAnnounce>();
  filteredAnnounces.forEach((announce) => {
    const key = `${announce.year}-${announce.month}`;
    const existing = latestForecastsMap.get(key);
    if (
      !existing ||
      (existing.manageInitiatedYearMonth &&
        announce.manageInitiatedYearMonth &&
        (existing.manageInitiatedYearMonth.year < announce.manageInitiatedYearMonth.year ||
          (existing.manageInitiatedYearMonth.year === announce.manageInitiatedYearMonth.year &&
            existing.manageInitiatedYearMonth.month < announce.manageInitiatedYearMonth.month)))
    ) {
      latestForecastsMap.set(key, announce);
    }
  });

  // ステップ3: 未来内示数を入れる
  futureMonthsScaffold.forEach((monthData) => {
    const key = `${monthData.year}-${monthData.month}`;
    const forecastData = latestForecastsMap.get(key);
    if (forecastData) {
      monthData.amount = forecastData.amount;
      monthData.manageInitiatedYearMonth = forecastData.manageInitiatedYearMonth;
      monthData.createAt = forecastData.createAt;
      monthData.updateAt = forecastData.updateAt;
    }
  });

  // ===== Step3: 在庫計算 =====

  const sortedInventory = [...(state.formSet.mvpInventory || [])]
    // 当月以降の在庫がもし入っていても無視する
    .filter((i) => DateService.compareYearMonths(i, DateService.getShiftedIYearMonthFromNowDateByMonths(0)) === -1)
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });

  let previousMonthEndInventory = sortedInventory.length > 0 ? sortedInventory[0].amount : state.formSet.inventory ?? 0;

  futureMonthsScaffold.forEach((m) => {
    const approvedOrdersAmount =
      state.formSet.recommend
        ?.filter(
          (item) =>
            item.status !== undefined &&
            item.status !== OrderStatus.Recommended &&
            item.status !== OrderStatus.ApprovalRequested &&
            item.status !== OrderStatus.Delivered &&
            item.status !== OrderStatus.DisplayEnded &&
            item.status !== OrderStatus.Unknown
        )
        .filter((item) => {
          if (!item.crossDate) return false;
          const d = DateService.convertIDateToDate(item.crossDate);
          return d.getFullYear() === m.year && d.getMonth() + 1 === m.month;
        })
        .reduce((sum, item) => sum + item.amount, 0) ?? 0;

    m.approvedOrdersAmount = approvedOrdersAmount;

    const endOfMonthInventory = previousMonthEndInventory + approvedOrdersAmount - m.amount;

    m.endOfMonthInventory = Math.round(endOfMonthInventory);

    m.inventoryTurnoverRate = m.amount > 0 ? Math.round((endOfMonthInventory / m.amount) * 100) / 100 : 0;

    previousMonthEndInventory = endOfMonthInventory;
  });

  return futureMonthsScaffold;
};

/**
 * 内示履歴と販売実績履歴をセットする
 */
const setHistoryValue = () => {
  const forecastDatesMap: { [key: string]: ForecastInfo } = forecastDateFunction(state.formSet.customerAnnounce || []);
  const salesResultHistoryMap: {
    [key: string]: ISalesResult;
  } = salesResultDateHistoryFunction(state.formSet?.salesResults || []);
  const inventoryHistoryMap: { [key: string]: IMvpInventory } = inventoryDateHistoryFunction(state.formSet?.mvpInventory || []);
  state.forecastDates = Object.values(forecastDatesMap)
    .sort(function (a, b) {
      const aValue = a.year * 100 + a.month;
      const bValue = b.year * 100 + b.month;
      return aValue - bValue;
    })
    .map((forecastDate) => {
      forecastDate.initialAmount = forecastDate.amount;
      return forecastDate;
    });
  pushForecastSnapshot(state.forecastDates);

  const forecastResultHistoryMap: {
    [key: string]: ForecastInfo;
  } = forecastDateHistoryFunction(state.formSetCustomerForecastHistory || []);

  state.forecastHistory = Object.values(forecastResultHistoryMap).sort(function (a, b) {
    const aValue = a.year * 100 + a.month;
    const bValue = b.year * 100 + b.month;
    return aValue - bValue;
  });
  // 日付の昇順に、とりあえず最新４月分の履歴を扱う
  state.currentMonthInputSalesResult = Object.values(salesResultHistoryMap).sort(function (a, b) {
    const aValue = a.year * 100 + a.month;
    const bValue = b.year * 100 + b.month;
    return aValue - bValue;
  });
  state.inventoryHistory = Object.values(inventoryHistoryMap).sort(function (a, b) {
    const aValue = a.year * 100 + a.month;
    const bValue = b.year * 100 + b.month;
    return aValue - bValue;
  });
};

/**
 * 当月の販売実績を計算する
 * @param salesResults - 全ての販売実績データ
 */
const calculateCurrentMonthSalesResult = (salesResults: ISalesResult[]): ISalesResult | undefined => {
  const now = dateStrToObject(new Date().getTime());
  return salesResults.find((v) => v.year == now.year && v.month == now.month);
};

// イベントフック
onMounted(async () => {
  await loadMyPropertyPreferences();
  state.isAppearenceUnpaidOrderedOrder =
    myPropertyPreferences.order.unpaidOrderedOrderAppearance && recommendOrderStore.state.unpaidOrderedOrder.length > 0;
  let prefRange = myPropertyPreferences['simulation']?.graphRange;
  if (prefRange !== undefined && prefRange > 180) prefRange = 180; // NOTE: 180日以上に万が一設定されていた場合は180日に設定する

  state.graphRange = prefRange ?? state.graphRange;
  state.inputGraphRange = [0, state.graphRange];

  const copyItem = Object.assign({}, props.item ?? {});
  if (props.item?.leadTime) {
    state.leadTimeSet = vm.getLeadTimeFormDataSet(props.item.leadTime);
  }
  if (props.item?.MOQ) {
    state.moqModelValue = props.item.MOQ;
  }

  // 客先内示情報の履歴情報を入れる
  const copyCustomerForecast = simpleCopy(props.formSetCustomerForecastHistory);
  state.formSetCustomerForecastHistory = copyCustomerForecast;

  // state.inventoryIndexSet.displayData = props.item ? props.item.stdInventoryIndices : undefined;
  state.inventoryIndexSet.displayData = undefined;
  const expectedQuantity = calculateRecommendOrderQuantity(copyItem);
  state.formSet = copyItem;
  state.stdIndexInfos = [];
  state.stdIndexInfos = stdInventoryIndexObjectToStdIndexInfos(copyItem.stdInventoryIndices || {});
  state.displayStdIndexInfo = stdInventoryIndexToDisplayStdIndexInfo(copyItem.stdInventoryIndices || {})[0];
  state.orderAmount = expectedQuantity as number;
  if (props.fixOrderValue) {
    state.orderAmount = props.fixOrderValue.orderAmount;
    state.deliveryDate = props.fixOrderValue.deliveryDate;
  }

  setHistoryValue();
  state.futureForecasts = calculateFutureForecasts(state.formSetCustomerForecastHistory);
  const currentMonthSalesResult = calculateCurrentMonthSalesResult(state.formSet?.salesResults || []);
  state.currentMonthData = currentMonthSalesResult
    ? {
        salesResult: currentMonthSalesResult,
        quantityRate: getQuantityRate(
          state.futureForecasts.find((v) => v.year == currentMonthSalesResult.year && v.month == currentMonthSalesResult.month)?.amount ??
            0,
          currentMonthSalesResult.amount
        ),
      }
    : undefined;
  renderChart();
  initQuantityRate();
  initTurnOver();
  // 内示計算用の割合の初期値 過去実績の平均正答率を入力する
  state.rate = isNaN(calcAverageQuantityRate()) ? 0 : Math.floor(calcAverageQuantityRate() * 10) / 1000;
  state.historyMonthOffset = 1;
});

watch(
  () => props.item,
  () => {
    renderChart();
  }
);
watch(
  () => state.selectedDateUnit,
  () => {
    state.graphRange = state.selectedDateUnit === 'month' ? MONTH_NUM_OF_BIZDAYS : DAY_NUM_OF_BIZDAYS;
    renderChart().then(() => (state.formSet = Object.assign({}, props.item ?? {})));
  }
);
watch(
  () => state.inputGraphRange,
  async () => {
    const rangeValue0 = state.inputGraphRange[0];
    const rangeValue1 = state.inputGraphRange[1];

    state.differenceFromStartDate = Math.min(rangeValue0, rangeValue1);
    const scale = Math.abs(rangeValue0 - rangeValue1);
    if (scale > 0) {
      state.graphRange = scale;
      // myPropertyPreferencesに保存
      myPropertyPreferences['simulation'] = {
        graphRange: scale,
      };
      await saveMyPropertyPreferences();
    } else {
      state.graphRange = 1; // 最低1日は表示する
    }
    renderChart();
  },
  { deep: true }
);
watch(
  () => props.extraSimulateInfos,
  () => {
    renderChart();
  }
);
watch(
  () => i18n.locale,
  () => {
    renderChart();
  }
);

/**
 * 表示情報を更新ボタンの活性化判定
 * NOTE: selectedInfoに差分がある場合＆selectedInfoが全て埋まっている状態（未選択は許容しない）
 */
const disableUpdateBusinessBtn = computed(() => {
  return (
    objectEquals(recommendOrderStore.state.selectedInitialInfo, recommendOrderStore.state.selectedInfo) ||
    recommendOrderStore.state.selectedInfo.supplierName === '' ||
    recommendOrderStore.state.selectedInfo.warehouse === '' ||
    recommendOrderStore.state.selectedInfo.businessCode === '' ||
    recommendOrderStore.state.selectedInfo.businessName === '' ||
    recommendOrderStore.state.selectedInfo.packageAmount === ''
  );
});
//関数
/**
 * チャートの再描画
 */
const renderChart = async () => {
  let business = simpleCopy(props.item ?? {});
  business.leadTime = state.formSet.leadTime;
  business.stdInventoryIndices = stdIndexInfosToStdInventoryIndexObject(state.stdIndexInfos);

  const orderAmount = state.orderAmount;
  state.workDaysCalendarNotYetEnteredAlertModalStatus = await shouldShowWorkDaysCalendarNotEnteredAlertModal();
  if (!state.isFirstRender) business = reflectForecastSimulation(business);
  state.isFirstRender = false;
  const simple =
    state.selectedDateUnit === 'month'
      ? await changeMonthChart(business, orderAmount, state.deliveryDate, props.extraSimulateInfos)
      : await changeDayChart(
          business,
          orderAmount,
          state.graphRange,
          state.differenceFromStartDate,
          dateStrToObject(props.item.executionDate ?? new Date()),
          props.extraSimulateInfos,
          getLatestArrivalDate(props.item)
        );
  // context.root.$set(state, 'chartData', simple);
  state.chartData = Object.assign({}, simple ?? { labels: [], datasets: [] });
  // 発注済み未納を追加する
  if (state.isAppearenceUnpaidOrderedOrder) {
    const unpaidOrderedOrderAmount = recommendOrderStore.state.unpaidOrderedOrder.reduce((acc, order) => {
      acc += order.amount;
      return acc;
    }, 0);
    state.chartData.datasets.push({
      label: t('未入荷在庫'),
      data: [unpaidOrderedOrderAmount, ...Array(179)],
      borderRadius: 5,
      backgroundColor: '#d50000',
      order: 999,
    });
  }
  reFleshRecommend(business);
  emits('updateSimulate', business.recommend);
};

/**
 * オーダーを変更する
 * @param orderAmount - オーダー数量
 */
const changeOrder = (orderAmount: number) => {
  state.orderAmount = orderAmount;
  renderChart();
};
/**
 * @param value - 数量
 * @param index - インデックス
 */
const forecastChange = (value, index) => {
  state.forecastDates[index].amount = value;
  renderChart();
};
/**
 * @param value - 数量
 */
const stdIndexChange = (value: StdIndexInfo) => {
  state.displayStdIndexInfo = value;
  state.stdIndexInfos[0] = simpleCopy(value);
  state.formSet = {
    ...state.formSet,
    stdInventoryIndices: { [value.name]: { min: value.min, max: value.max, option: { unit: value.unit } } },
  };
  renderChart();
};
const vm = new TableContentViewModel();
// リードタイムの変更を検知して、希望納期に反映させる
/**
 * @param value - リードタイム
 */
const leadTimeChange = (value: ISelectFormDataSet) => {
  state.leadTimeSet = value;
  const leadTime = simpleCopy(state.leadTimeSet);
  state.formSet = {
    ...state.formSet,
    leadTime: calculateDataToDays(leadTime),
  };

  state.deliveryDate = dateStrToObject(
    new Date().getTime() + (calculateDataToDays(leadTime) >= 0 ? calculateDataToDays(leadTime) * DAY_STD : 0)
  );
  renderChart();
};
/**
 */
const orderFormEl = () => {
  const dialog = document.getElementsByClassName('md-datepicker-dialog');
  if (dialog.length > 0) {
    (dialog[0] as any).style.opacity = '0.5';
    /**
     */
    (dialog[0] as any).onclick = function () {
      (dialog[0] as any).style.opacity = '1';
    };
  }
  state.isActiveForecastButtons = false;
};
/**
 */
const onClickDatePicker = () => {
  const dialog = document.getElementsByClassName('md-datepicker-dialog');
  if (dialog.length > 0) {
    (dialog[0] as any).style.opacity = '1';
  }
};
/**
 * @param business - 取引
 */
const reflectForecastSimulation = (business: IBusiness) => {
  let realAnnounce: IAnnounce[] = [];
  const realAnnounceMap: { [key: string]: IAnnounce } = {};
  business.customerAnnounce &&
    business.customerAnnounce.forEach((a) => {
      // サンプルデータにバグがあったのでわざわざこんなことを行っているが本当は自明なので行わなくてよい。
      // 重複排除
      const key = a.year * YEAR_TO_MONTH + a.month;
      if (realAnnounceMap[key] === undefined) {
        realAnnounceMap[key] = a;
      } else {
        realAnnounceMap[key].amount = a.amount;
      }
    });
  realAnnounce = Object.values(realAnnounceMap).sort(function (a, b) {
    // サンプルデータにバグがあったのでわざわざこんなことを行っているが本当は自明なので行わなくてよい。
    // 順序
    const aValue = a.year * 100 + a.month;
    const bValue = b.year * 100 + b.month;
    return aValue - bValue;
  });
  let simulationForecastIndex = 0;
  let realBusinessIndex = 0;
  const adaptAnnounce: IAnnounce[] = [];
  while (true) {
    // 尺取法
    const realValue = realAnnounce[realBusinessIndex];
    const simulationValue = state.forecastDates[simulationForecastIndex];
    if (realValue !== undefined && simulationValue !== undefined) {
      // 両方とも存在する時
      const realTime = realValue.year * YEAR_TO_MONTH + realValue.month;
      const simulationTime = simulationValue.year * YEAR_TO_MONTH + simulationValue.month;
      if (realTime == simulationTime) {
        adaptAnnounce.push(simulationValue); //　被った場合はsimulationを適応
        simulationForecastIndex++;
        realBusinessIndex++;
      } else if (simulationTime < realTime) {
        //　それ以外では比較して、過去になるものを採用
        adaptAnnounce.push(simulationValue);
        simulationForecastIndex++;
      } else {
        adaptAnnounce.push(realValue);
        realBusinessIndex++;
      }
    } else {
      if (realValue === undefined && simulationValue === undefined) {
        break;
      } else if (simulationValue === undefined) {
        adaptAnnounce.push(realValue);
        realBusinessIndex++;
      } else if (realValue === undefined) {
        adaptAnnounce.push(simulationValue);
        simulationForecastIndex++;
      }
    }
  }
  business.customerAnnounce = adaptAnnounce;
  return business;
};
/**
 *　　モーダルを閉じる
 */
const closeModal = () => {
  closeWorkDaysCalendarNotYetEnteredAlertModal();
  emits('cancel', false);
};

/**
 *  モーダルを閉じる
 */
const closeWorkDaysCalendarNotYetEnteredAlertModal = () => {
  state.workDaysCalendarNotYetEnteredAlertModalStatus = false;
};

/**
 * 実働勘案内示数量の計算を行う
 * 計算後は計算結果をチャートに反映させる
 */
const adaptRate = () => {
  state.isActiveForecastButtons = false;
  const forecastDates = simpleCopy(state.forecastDates);
  let canAdaptRate = true;
  switch (state.operator) {
    case 'multiplication':
      for (let i = 0; i < forecastDates.length; i++) {
        forecastDates[i].amount *= Number(state.rate);
        forecastDates[i].amount = Math.floor(forecastDates[i].amount + 0.5);
        if (canAdaptRate) {
          canAdaptRate = forecastDates[i].amount.isValidNumber({ upper: FORECAST_UPPER_LIMIT });
        }
      }
      break;
    case 'division':
      for (let i = 0; i < forecastDates.length; i++) {
        forecastDates[i].amount /= Number(state.rate);
        forecastDates[i].amount = Math.floor(forecastDates[i].amount + 0.5);
        if (canAdaptRate) {
          canAdaptRate = forecastDates[i].amount.isValidNumber({ upper: FORECAST_UPPER_LIMIT });
        }
      }
      break;
  }
  if (!canAdaptRate) {
    // TODO: 上限を超えるため更新できない旨をアラートで知らせる
    return;
  }
  if (!objectEquals(state.forecastDates, forecastDates)) {
    state.forecastDates = forecastDates;
    pushForecastSnapshot(state.forecastDates);
    renderChart();
    resetForecastRate();
  }
};

/**
 * 内示数量の更新履歴をスタックに追加
 * @param snapshot - 新規更新履歴
 */
const pushForecastSnapshot = (snapshot: ForecastInfo[]) => {
  // 新しい情報を追加する際、現在より先の情報は破棄する
  if (state.currentForecastIndex !== state.forecastDatesStack.length - 1) {
    state.forecastDatesStack = state.forecastDatesStack.slice(0, state.currentForecastIndex + 1);
  }
  // 変更履歴は最大10件まで記録しておく
  if (state.forecastDatesStack.length === 10) {
    // 10件溜まっていたら一番古いものから削除
    state.forecastDatesStack.shift();
  }
  state.forecastDatesStack.push(snapshot);
  // 表示する最新の情報が入っているindexを更新する
  state.currentForecastIndex = state.forecastDatesStack.length - 1;
};

/**
 * 現在、戻るボタンが有効かどうかを確認する関数
 * @returns 戻るボタンが有効かどうか
 */
const canUndoForecastStack = (): boolean => {
  return state.currentForecastIndex !== 0;
};

/**
 * 現在、進むボタンが有効かどうかを確認する関数
 * @returns 進むボタンが有効かどうか
 */
const canRedoForecastStack = (): boolean => {
  return state.currentForecastIndex !== state.forecastDatesStack.length - 1;
};

/**
 * Undo(戻る)機能。内示数量を一つ前の更新履歴で上書きする関数
 */
const undoForecastStack = () => {
  if (!canUndoForecastStack()) {
    return;
  }
  state.currentForecastIndex--;
  state.forecastDates = simpleCopy(state.forecastDatesStack[state.currentForecastIndex]);
  renderChart();
};

/**
 * Redo(進む)機能。内示数量を一つ後の更新履歴で上書きする関数
 */
const redoForecastStack = () => {
  if (!canRedoForecastStack()) {
    return;
  }
  state.currentForecastIndex++;
  state.forecastDates = simpleCopy(state.forecastDatesStack[state.currentForecastIndex]);
  renderChart();
};

/**
 * 内示数量を初期値に戻す
 */
const resetForecastCalculationResults = () => {
  if (state.forecastDates.every((forecast) => forecast.amount === forecast.initialAmount)) {
    return;
  }
  state.forecastDates = state.forecastDates.map((forecast) => {
    forecast.amount = forecast.initialAmount;
    return forecast;
  });
  pushForecastSnapshot(state.forecastDates);
  renderChart();
};

/**
 * 乗算/除算切り替え時にstate.operatorを切り替える
 * @param operator - 乗算/除算の処理分岐に使う値
 */
const updateOperator = (operator: string) => {
  state.isActiveForecastButtons = false;
  state.operator = operator;
};
/**
 * @param value - 数量
 */
const setDetliveryDate = (value: number) => {
  state.deliveryDate = dateStrToObject(value);
  renderChart();
};
/**
 * @param input - 取引
 */
const reFleshRecommend = (input: IBusiness) => {
  state.formSet.recommend = input.recommend ?? [];
};
const displayDeliveryDate = computed({
  /**
   * @returns - 表示用の納期
   */
  get: () => dateObjToUnixTime(state.deliveryDate) || new Date().getTime(),
  /**
   * @param value - 納期
   */
  set: (value: number) => setDetliveryDate(value),
});

/**
 * 内示/販売実績の正答率計算を行う
 * 計算で、分母に0などを行い値がNaNになった場合は、'-'を返すようにする
 * @param forevastValue - 内示の値
 * @param salesResultValue - 販売実績の値
 */
const getQuantityRate = (forevastValue: number, salesResultValue: number) => {
  // 0除算の回避 分母に0が入る場合infinityになるため、そのような場合は0を返す
  const quantityRate: number = forevastValue !== 0 ? (salesResultValue / forevastValue) * 100 : 0;
  if (!Number.isNaN(quantityRate) && quantityRate !== 0) {
    return Math.floor(quantityRate * 10) / 10;
  } else {
    return 0;
  }
};

/**
 * 内示/販売実績の正答率計算を行う
 * 計算で、分母に0などを行い値がNaNになった場合は、'-'を返すようにする
 */
const initQuantityRate = (): void => {
  state.forecastHistory.forEach((value, index) => {
    const forecastValue = value.amount ?? 0;
    const salesResultValue = state.currentMonthInputSalesResult[index].amount ?? 0;
    state.quantityRate.push(getQuantityRate(forecastValue, salesResultValue));
  });
};

/**
 * 過去の在庫回転率情報の初期化
 * 在庫回転率は、在庫量 / 内示量で計算される
 * 在庫量が0または内示量が0の場合は、0を入れる
 * 在庫回転率の計算結果は小数点以下2桁まで表示する
 */
const initTurnOver = (): void => {
  state.forecastHistory.forEach((value, index) => {
    const forecastValue = value.amount ?? 0;
    const inventoryValue = state.inventoryHistory[index].amount ?? 0;
    // 0除算の回避 分母に0が入る場合infinityになるため、そのような場合は0を入れる
    if (forecastValue === 0 || inventoryValue === 0) {
      state.turnOverHistory.push({
        year: value.year,
        month: value.month,
        turnOver: 0,
      });
      return;
    }
    // 在庫回転率の計算
    const turnOver: number = inventoryValue / forecastValue;
    if (!Number.isNaN(turnOver) && turnOver !== 0) {
      state.turnOverHistory.push({
        year: value.year,
        month: value.month,
        turnOver: Math.floor(turnOver * 100) / 100, // 小数点以下2桁まで表示
      });
    } else {
      state.turnOverHistory.push({
        year: value.year,
        month: value.month,
        turnOver: 0,
      });
    }
  });
};

/**
 * 内示/販売実績の正答率の平均値を計算して出力する
 * 出力する際は、%表示するために割合*100して、文字列にして返す
 */
const calcAverageQuantityRate = (): number => {
  // 過去12ヶ月分の内示合計
  const forecastTotal = state.forecastHistory.reduce((sum, ele) => {
    return sum + ele.amount;
  }, 0);
  // 過去12ヶ月分の販売実績合計
  const salesResultTotal = state.currentMonthInputSalesResult.reduce((sum, ele) => {
    return sum + ele.amount;
  }, 0);

  return forecastTotal !== 0 ? Math.floor((salesResultTotal / forecastTotal) * 1000) / 10 : 0;
};
/**
 *　内示/販売実績の正答率の平均値を計算して出力する
 */
const switchAnnounceMode = () => {
  state.isRenderForecastHistory = !state.isRenderForecastHistory;
};
const showAnnounceHistory = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.forecastHistory;
  } else {
    return [];
  }
});
const showSalesResultHistory = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.currentMonthInputSalesResult;
  } else {
    return [];
  }
});
const showInventoryHistory = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.inventoryHistory;
  } else {
    return [];
  }
});
const showRecommendDate = computed(() => {
  if (state.formSet.recommend) {
    return state.formSet.recommend;
  } else {
    return state.formSet.recommend;
  }
});
const showRecommendAmount = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.currentMonthInputSalesResult;
  } else {
    return [];
  }
});
const showQuantityRate = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.quantityRate;
  } else {
    return [];
  }
});
const showTurnOverHistory = computed(() => {
  if (state.isRenderForecastHistory) {
    return state.turnOverHistory;
  } else {
    return [];
  }
});
/**
 *　 @param operater - オペレータ
 */
const showGraphRange = (operater: string): string => {
  const baseDate = new Date(props.item.executionDate ?? new Date().getTime());

  if (operater === 'right') {
    // 現在より180日分表示するようにする
    baseDate.setDate(baseDate.getDate() + 180);
  }
  const yearStr = baseDate.getFullYear();
  //月だけ+1すること
  const monthStr = baseDate.getMonth() + 1;
  const dayStr = baseDate.getDate();
  let formatStr = 'YYYY-MM-DD';
  formatStr = formatStr.replace(/YYYY/g, yearStr.toString());
  formatStr = formatStr.replace(/MM/g, monthStr.toString());
  formatStr = formatStr.replace(/DD/g, dayStr.toString());
  return formatStr;
};
const styles = {
  formInput: {
    width: '47%',
  },
  deliveryArea: {
    paddingTop: '20px',
    width: '60%',
    margin: 'auto',
  },
  amountLabel: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  datePickerLabel: {
    fontSize: '24px',
    fontWeight: 'bold',
    width: '57%',
  },
  labelSquareStyle: {
    width: '21px',
  },
  selectField: {
    marginLeft: '10px',
  },
  rateInput: {
    overflow: 'hidden',
    border: 'solid #94a3b8',
    borderWidth: '2px',
    paddingRight: '0.25rem',
    paddingLeft: '0.25rem',
  },
};

/**
 * 内示計算用の割合を変更する
 * @param value - 内示計算用の割合
 */
const rateChange = (value) => {
  state.rate = value;
};

/**
 * 内示計算用の割合をリセットする
 */
const resetForecastRate = () => {
  // 内示計算用の割合を等倍に戻す（連打による連続回計算処理の防止策）
  state.rate = EQUAL_RATE;
};

/**
 * 関連取引モーダルを開く
 */
const openRelatedTransactionsModal = async () => {
  await recommendOrderStore.openRelatedTransactionsModal(props.item.businessId);
};

/**
 * リードタイムピッカーを開く
 * @param event - ボタンのクリックイベント
 */
const openLeadTimePicker = (event: MouseEvent) => {
  state.leadTimePickerState.originCoordinate.x = event.clientX;
  state.leadTimePickerState.originCoordinate.y = event.clientY;
  state.leadTimePickerState.shouldShow = true;
};
/**
 * リードタイムピッカーを閉じる
 */
const closeLeadTimePicker = () => {
  state.leadTimePickerState.shouldShow = false;
};
/**
 * リードタイムピッカーで選択した値を反映させる
 * @param value - リードタイムピッカーで選択した値
 */
const onConfirmLeadTimePicker = (value: ISelectFormDataSet) => {
  leadTimeChange(value);
  closeLeadTimePicker();
};

/**
 * 標準在庫指数ピッカーを開く
 * @param event - ボタンのクリックイベント
 */
const openStdInventoryPicker = (event: MouseEvent) => {
  state.stdInventoryPickerState.originCoordinate.x = event.clientX;
  state.stdInventoryPickerState.originCoordinate.y = event.clientY;
  state.stdInventoryPickerState.shouldShow = true;
};
/**
 * 標準在庫指数ピッカーを閉じる
 */
const closeStdInventoryPicker = () => {
  state.stdInventoryPickerState.shouldShow = false;
};
/**
 * 標準在庫指数ピッカーで選択した値を反映させる
 * @param value - 標準在庫指数ピッカーで選択した値
 */
const onStdInventoryPicker = (value: StdIndexInfo) => {
  stdIndexChange(value);
  closeStdInventoryPicker();
};

//computed
const orderChartStyles = computed(() => {
  return {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  };
});

/**
 * csv出力関数に渡すためにグラフ出力情報を整理する
 * string型の2次元配列の作成を行なう
 * @param formSet - シミュレーションの結果
 * @param chartData - グラフの出力情報
 * @param extraSimulateInfos - シミュレーションの結果(リードタイムと標準在庫指数を取るために必要)
 */
const createChartDataForCsv = (formSet: IBusiness, chartData: IChartdata, extraSimulateInfos?: IExtraSimulate): CreateCsvInfo => {
  // 出力用の二次元配列
  const outputArray: string[][] = [];

  // 取引概要の情報を配列に入れる
  const businessInfoArray: string[] = [formSet.warehouse, formSet.supplierName, formSet.businessName ?? '', formSet.package];
  outputArray.push(businessInfoArray, ['']); // 取引概要と空行を追加する

  // リードタイムと標準在庫指数の追加を行う
  // TODO: formSetだと、画面で値を変えた後の情報を取得できない
  const leadTimeAndStdIndex = [
    vm.displayTableContent(nullOrUndefinedToMinusOne(extraSimulateInfos?.extraLeadTime ?? 0)),
    vm.getDisplayStdInventory(formSet, extraSimulateInfos?.extraStdInventory),
  ];
  // 出力用配列に、タイトル、それぞれの情報、空行を追加する
  outputArray.push(['リードタイム', '標準在庫指数'], leadTimeAndStdIndex, ['']);

  // 客先内示の追加を行う
  outputArray.push(['客先内示']); // タイトル行

  // 客先内示情報の取得
  if (extraSimulateInfos?.extraAnnounce) {
    const customerAnnounceInfo: IAnnounce[] = [];

    // 年月が条件に一致する客先内示情報のみに絞る
    for (let i = 0; i < 4; i++) {
      const targetYearMonth: IYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(i);
      extraSimulateInfos?.extraAnnounce.forEach((ele) => {
        if (DateService.equalYearMonthInfo({ year: ele.year, month: ele.month }, targetYearMonth)) {
          customerAnnounceInfo.push(ele);
        }
      });
    }
    outputArray.push(
      ['年月'].concat(
        customerAnnounceInfo.map((info) => DateService.convertIYearMonthToYYYYMMDivideSlash({ year: info.year, month: info.month }))
      )
    );
    outputArray.push(['内示数量'].concat(customerAnnounceInfo.map((info) => String(info.amount))));
  }

  // 空行を追加する
  outputArray.push([''], ['グラフデータ']);

  // グラフ構成情報
  // ラベル(年月情報)を配列に追加していく、一番左の列は、行タイトル分開ける
  const labelArray: string[] = ['年月日'].concat(chartData.labels.map(String)); // 行タイトル分1列空列を入れる
  // 出力用の配列に追加する
  outputArray.push(labelArray);

  // グラフを構成するデータセットの追加
  chartData.datasets.forEach((dataset) => {
    // 先に行タイトルおよびグラフを構成するデータを配列に追加する
    const datasetArray = [dataset.label ?? ''].concat(dataset.data.map((data) => String(Math.floor(data ?? 0))));
    outputArray.push(datasetArray);
  });

  // csv作成用のオブジェクトに情報を追加
  const title = businessInfoArray.join(' ');
  return { data: outputArray, title: `グラフデータ_${title}` };
};

/**
 * string型の2次元配列を受けとってcsvファイル作成関数に投げる
 * TODO: exportFileManagerに移動させる
 */
const exportChartDataCsv = async () => {
  const input: CreateCsvInfo = createChartDataForCsv(state.formSet, state.chartData as IChartdata, props.extraSimulateInfos);
  const res = await createChartDataForCsvUseCase.Execute(input);
  switch (res.status.type) {
    case 'success':
      break;
    case 'error':
      await openFailToSavedModal();
      break;
  }
};

/**
 * 過去実績の月を変更する
 * @param value - 変更後の値
 */
const historyMonthOffsetUpdate = (value: string) => {
  const newOffset = Number(value.charAt(0));
  state.historyMonthOffset = newOffset;
  setHistoryValue();
  state.futureForecasts = calculateFutureForecasts(state.formSetCustomerForecastHistory);
  const currentMonthSalesResult = calculateCurrentMonthSalesResult(state.formSet?.salesResults || []);
  state.currentMonthData = currentMonthSalesResult
    ? {
        salesResult: currentMonthSalesResult,
        quantityRate: getQuantityRate(
          state.futureForecasts.find((v) => v.year == currentMonthSalesResult.year && v.month == currentMonthSalesResult.month)?.amount ??
            0,
          currentMonthSalesResult.amount
        ),
      }
    : undefined;
  state.quantityRate = [];
  state.turnOverHistory = [];
  initQuantityRate();
  initTurnOver();
};

/**
 * MOQを指定するポップアップを開く
 * @param event - マウスイベント
 */
const openMOQInput = (event: MouseEvent) => {
  const moqContent = document.getElementsByClassName(
    'order-form-simulation-graph-button-container__moq-info__text-button'
  )[0] as HTMLElement;
  state.moqInputPopUpState.originCoordinate.x = moqContent.offsetLeft;
  state.moqInputPopUpState.originCoordinate.y = moqContent.offsetTop + moqContent.offsetHeight;
  state.moqInputPopUpState.shouldShow = true;
};

/**
 * 発注単位を指定するポップアップを開く
 * @param event - マウスイベント
 */
const openOrderPackageInput = (event: MouseEvent) => {
  const orderPackageContent = document.getElementsByClassName(
    'order-form-simulation-graph-button-container__order-package-info__text-button'
  )[0] as HTMLElement;
  state.orderPackageInputPopUpState.originCoordinate.x = orderPackageContent.offsetLeft;
  state.orderPackageInputPopUpState.originCoordinate.y = orderPackageContent.offsetTop + orderPackageContent.offsetHeight;
  state.orderPackageInputPopUpState.shouldShow = true;
};

/**
 * MOQを指定するポップアップを閉じる
 */
const closeMOQInput = () => {
  state.moqModelValue = props.item?.MOQ ?? 0;
  state.moqInputPopUpState.shouldShow = false;
};

/**
 * 発注単位を指定するポップアップを閉じる
 */
const closeOrderPackageInput = () => {
  state.orderPackageModelValue = props.item?.orderPackage ?? 0;
  state.orderPackageInputPopUpState.shouldShow = false;
};

/**
 * MOQを更新する
 */
const updateMOQ = async () => {
  const result = await recommendOrderStore.onUpdateMoq(state.moqModelValue);
  if (result) state.moqInputPopUpState.shouldShow = false;
};

/**
 * 発注単位を更新する
 */
const updateOrderPackage = async () => {
  const result = await recommendOrderStore.onUpdateOrderPackage(state.orderPackageModelValue);
  if (result) state.orderPackageInputPopUpState.shouldShow = false;
};

/**
 * メモを更新する
 * @param value - メモのテキスト
 */
const updateMemo = async (value: string) => {
  await recommendOrderStore.updateMemo(value);
};

/**
 * メモを削除する
 */
const deleteMemo = async () => {
  await recommendOrderStore.deleteMemo();
};

/**
 * ページングボタンの活性/非活性を制御する
 * @param type - ページングの向き
 */
const pagingButtonDisabledControl = (type: 'previous' | 'next') => {
  const aggregateBusinessIdsHasRecommendOrderIndex = useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.indexOf(
    state.formSet.businessId
  );
  if (type === 'previous') {
    return aggregateBusinessIdsHasRecommendOrderIndex === 0;
  } else {
    return aggregateBusinessIdsHasRecommendOrderIndex === useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.length - 1;
  }
};

/**
 * ページングを更新する
 * @param type - ページングの向き
 */
const pagingUpdate = (type: 'previous' | 'next') => {
  const pageIndex = useSelectedBusinessState.recommendOrderPagingOption.pageIndex;
  if (type === 'previous') {
    const changePageIndex = pageIndex - 1 < 0 ? 0 : pageIndex - 1;
    useSelectedBusinessState.recommendOrderPagingOption.pageIndex = changePageIndex;
    emits('pagingUpdate', useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder[changePageIndex]);
  } else {
    const changePageIndex =
      pageIndex + 1 >= useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.length
        ? useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.length - 1
        : pageIndex + 1;
    useSelectedBusinessState.recommendOrderPagingOption.pageIndex = changePageIndex;
    emits('pagingUpdate', useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder[changePageIndex]);
  }
};

/**
 * 任意項目の値を表示する
 * 対象の値: MOQ、発注単位
 * @param value - 任意項目の値
 * @returns - 任意項目の値を表示する文字列
 */
const displayOptionalItemValue = (value: number | undefined): string => {
  return value ? value.addComma() : '-';
};
</script>

<template>
  <WorkDaysCalendarNotYetEnteredAlertModal
    :is-open-modal="state.workDaysCalendarNotYetEnteredAlertModalStatus"
    @outside-click="closeWorkDaysCalendarNotYetEnteredAlertModal()"
  />
  <div class="order-form" @click="orderFormEl">
    <!-- 取引概要部分の領域 -->
    <div class="page-top order-from__page-top">
      <div class="page-top__table-title">
        {{ t('取引概要(詳細ページ)') }}
      </div>
      <div v-if="useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.length > 0" class="page-top__page-nation">
        <button class="page-top__page-nation--arrow" :disabled="pagingButtonDisabledControl('previous')" @click="pagingUpdate('previous')">
          <SvgLoader name="arrowLeft" class="page-move__left" />
          <span class="page-top__page-nation--arrow-text">
            {{ t('前へ') }}
          </span>
        </button>
        <button class="page-top__page-nation--arrow" :disabled="pagingButtonDisabledControl('next')" @click="pagingUpdate('next')">
          <span class="page-top__page-nation--arrow-text">
            {{ t('次へ') }}
          </span>
          <SvgLoader name="arrowRight" class="page-move__right" />
        </button>
        <span class="page-top__page-nation--current-page">
          {{
            `${useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.indexOf(state.formSet.businessId) + 1}/${
              useSelectedBusinessState.aggregateBusinessIdsHasRecommendOrder.length
            }`
          }}
        </span>
      </div>
    </div>
    <div class="business-transaction">
      <div class="business-transaction__table">
        <SelectableSimpleTableContent
          :table-type="'simpleBusinessSummary'"
          :business="state.formSet"
          :editable-lead-time="true"
          :editable-std-inventory="true"
          :table-body-font-size="'fontSizeS'"
          @on-click-lead-time-button="openLeadTimePicker"
          @on-click-std-inventory-button="openStdInventoryPicker"
        />
      </div>
    </div>
    <div class="order-form-simulation-graph-button-container">
      <div class="order-form-simulation-graph-button-container__weight-info">
        <div class="order-form-simulation-graph-button-container__weight-info-row">
          <div class="order-form-simulation-graph-button-container__weight-info--unit">
            <p>{{ t('製造社') + ` : ${props.item.manufacturer ? props.item.manufacturer : '--'}` }}</p>
          </div>
          <div class="order-form-simulation-graph-button-container__weight-info--latest-inventory">
            <p>{{ t('最新在庫数(詳細ページ)') + ` : ${props.item.inventory?.addComma()}` }}</p>
          </div>
          <div class="order-form-simulation-graph-button-container__moq-info">
            <div class="order-form-simulation-graph-button-container__moq-info__text-button" @click="openMOQInput">
              <p>{{ t('MOQ(詳細ページ)') + ` : ${displayOptionalItemValue(props.item.MOQ)}` }}</p>
            </div>
          </div>
          <div class="order-form-simulation-graph-button-container__order-package-info">
            <div class="order-form-simulation-graph-button-container__order-package-info__text-button" @click="openOrderPackageInput">
              <p>{{ t('発注単位(詳細ページ)') + ` : ${displayOptionalItemValue(props.item.orderPackage)}` }}</p>
            </div>
          </div>
          <div class="order-form-simulation-graph-button-container__memo-info">
            <TableMemoButton
              :memo="props.item.note"
              :need-confirm-deletion="false"
              :need-label-text="true"
              @save="updateMemo"
              @delete="deleteMemo"
            />
          </div>
          <button class="accordion-toggle-button" @click="isExpanded = !isExpanded">
            <Mdi icon="expand_more" class="accordion-toggle-icon" :class="{ 'is-expanded': isExpanded }" />
          </button>
        </div>
        <div v-show="isExpanded" class="order-form-simulation-graph-button-container__weight-info-row">
          <div class="order-form-simulation-graph-button-container__weight-info--responsible-users">
            <OptimizedTooltipText>
              {{ t('仕入担当者') + ' : ' }}
              <span v-if="props.item.aggregateBusinessResponsibleUsers && props.item.aggregateBusinessResponsibleUsers.length > 0">
                <span v-for="(user, index) in props.item.aggregateBusinessResponsibleUsers" :key="user.email">
                  {{ user.name }}{{ index < props.item.aggregateBusinessResponsibleUsers.length - 1 ? ', ' : '' }}
                </span>
              </span>
              <span v-else> -- </span>
            </OptimizedTooltipText>
          </div>
        </div>
      </div>
      <MoqInputPopUp
        class="moq-input-popup"
        :model-value="state.moqModelValue"
        :pop-up-state="state.moqInputPopUpState.shouldShow"
        @update:model-value="(value) => (state.moqModelValue = value)"
        @close="closeMOQInput"
        @confirm="updateMOQ"
      />
      <OrderPackageInputPopUp
        class="order-package-input-popup"
        :model-value="state.orderPackageModelValue"
        :pop-up-state="state.orderPackageInputPopUpState.shouldShow"
        @update:model-value="(value) => (state.orderPackageModelValue = value)"
        @close="closeOrderPackageInput"
        @confirm="updateOrderPackage"
      />
      <TextButton
        class="order-form-simulation-graph-button-container__update-button"
        color="primary"
        :size="'btn-md'"
        :disabled="disableUpdateBusinessBtn"
        @click="emits('updateBusinessId')"
      >
        {{ t('表示情報を更新(詳細ページ)') }}
        <SvgLoader
          class="order-form-simulation-graph-button-container__update-button--loop-icon"
          :name="'loopIcon'"
          data-testid="loop_icon"
        />
      </TextButton>
    </div>
    <!-- 過去実績部分の領域 -->
    <div class="forecast-history__label-container">
      <div :data-testid="`forecast_history_label`" class="order-form__table-title">
        {{ t('過去実績一覧(詳細ページ)') }}
      </div>
      <div class="forecast-history__label-inner-left">
        <div class="forecast-history__label-change-month">
          <SimpleSelectInput
            class="forecast-history__label-change-month-input"
            :index="1"
            select-box-name="SELECT_PAST_FORCAST_FOR_RECEIPT_MONTH"
            :model-value="state.historyMonthOffset + 'ヶ月前内示'"
            :prop-options="['1ヶ月前内示', '2ヶ月前内示', '3ヶ月前内示']"
            display-allow
            @update:model-value="historyMonthOffsetUpdate"
          />
        </div>
        <div class="forecast-history__label-percentage">
          <p class="forecast-history__label-percentage--tag">
            {{ t('平均正答率(詳細ページ)') + ` : ` }}
          </p>
          <p class="forecast-history__label-percentage--number">
            {{ vm.displayFromNumberToString(calcAverageQuantityRate()) }}
          </p>
          <p class="forecast-history__label-percentage--mark">%</p>
        </div>
      </div>
    </div>
    <div class="forecast-history">
      <div :data-testid="`forecast_history_frame`" class="table-border__style overflow-scroll">
        <table class="forecast-history__table border-collapse bg-white text-sm text-gray-500">
          <thead class="bg-gray-300">
            <tr>
              <TableHead
                :font-size="'fontSizeS'"
                :background-color="'bg-default'"
                class="forecast-history__table--side-header-empty px-2 font-semibold sticky"
              >
                {{ t('') }}
              </TableHead>
              <TableHead
                v-for="(property, index) of showAnnounceHistory"
                :key="`first-${index}`"
                :text-align="'left'"
                :font-size="'fontSizeS'"
                :background-color="'bg-default'"
              >
                {{ t('displayShortDate', { year: property.year, month: DateService.convertMonthWithZero(property.month) }) }}
              </TableHead>
              <TableHead
                v-for="(property, index) of state.futureForecasts"
                :key="`future-header-${index}`"
                :text-align="'left'"
                :font-size="'fontSizeS'"
                :background-color="'bg-default'"
              >
                {{ t('displayShortDate', { year: property.year, month: DateService.convertMonthWithZero(property.month) }) }}
              </TableHead>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TableHead
                :font-size="'fontSizeS'"
                :background-color="'bg-default'"
                class="forecast-history__table--side-header px-2 py-1 sticky"
              >
                {{ t('正答率(詳細ページ)') }}
              </TableHead>
              <template v-for="(property, index) of showQuantityRate" :key="`first-${index}`">
                <TableCell
                  :tool-tip-value="vm.displayFromNumberToString(property) + '%'"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ vm.displayFromNumberToString(property) + '%' }}
                </TableCell>
              </template>
              <TableCell
                :tool-tip-value="vm.displayFromNumberToString(state.currentMonthData?.quantityRate) + '%'"
                :font-size="'fontSizeS'"
                :is-border="false"
                class="forecast-history__table--cell-value"
              >
                {{ vm.displayFromNumberToString(state.currentMonthData?.quantityRate) + '%' }}
              </TableCell>
              <template v-for="(property, index) of state.futureForecasts.slice(0, -1)" :key="`future-accuracy-${index}`">
                <TableCell :tool-tip-value="'-'" :font-size="'fontSizeS'" :is-border="false" class="forecast-history__table--cell-value">
                  -
                </TableCell>
              </template>
            </tr>
            <tr>
              <TableHead :font-size="'fontSizeS'" :background-color="'bg-default'" class="forecast-history__table--side-header sticky">
                {{ t('在庫回転率(詳細ページ)') }}
              </TableHead>
              <template v-for="(property, index) of showTurnOverHistory" :key="`first-${index}`">
                <TableCell
                  :tool-tip-value="property.turnOver.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.turnOver.addComma() }}
                </TableCell>
              </template>
              <template v-for="(property, index) of state.futureForecasts" :key="`future-turnover-${index}`">
                <TableCell
                  :tool-tip-value="property.inventoryTurnoverRate.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.inventoryTurnoverRate.addComma() }}
                </TableCell>
              </template>
            </tr>
            <tr>
              <TableHead :font-size="'fontSizeS'" :background-color="'bg-default'" class="forecast-history__table--side-header sticky">
                <span class="forecast-history__table--cell-link" @click="openRelatedTransactionsModal()">
                  {{ t('販売実績(詳細ページ)') }}
                </span>
              </TableHead>
              <template v-for="(property, index) of showSalesResultHistory" :key="`first-${index}`">
                <TableCell
                  :tool-tip-value="property.amount.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.amount.addComma() }}
                </TableCell>
              </template>
              <TableCell
                :tool-tip-value="state.currentMonthData?.salesResult.amount.addComma() ?? '-'"
                :font-size="'fontSizeS'"
                :is-border="false"
                class="forecast-history__table--cell-value"
              >
                {{ state.currentMonthData?.salesResult.amount.addComma() ?? '-' }}
              </TableCell>
              <template v-for="(property, index) of state.futureForecasts.slice(0, -1)" :key="`future-sales-${index}`">
                <TableCell :tool-tip-value="'-'" :font-size="'fontSizeS'" :is-border="false" class="forecast-history__table--cell-value">
                  -
                </TableCell>
              </template>
            </tr>
            <tr>
              <TableHead
                :font-size="'fontSizeS'"
                :background-color="'bg-default'"
                class="forecast-history__table--side-header px-2 py-1 sticky"
              >
                <span class="forecast-history__table--cell-link" @click="openRelatedTransactionsModal()">
                  {{ t('内示(詳細ページ)') }}
                </span>
              </TableHead>
              <template v-for="(property, index) of showAnnounceHistory" :key="`first-${index}`">
                <TableCell
                  :tool-tip-value="property.amount.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.amount.addComma() }}
                </TableCell>
              </template>
              <template v-for="(property, index) of state.futureForecasts" :key="`future-forecast-${index}`">
                <TableCell
                  :tool-tip-value="(property.amount ?? 0).addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ (property.amount ?? 0).addComma() }}
                </TableCell>
              </template>
            </tr>
            <tr>
              <TableHead :font-size="'fontSizeS'" :background-color="'bg-default'" class="forecast-history__table--side-header sticky">
                {{ t('月末在庫(詳細ページ)') }}
              </TableHead>
              <template v-for="(property, index) of showInventoryHistory" :key="`first-${index}`">
                <TableCell
                  :tool-tip-value="property.amount.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.amount.addComma() }}
                </TableCell>
              </template>
              <template v-for="(property, index) of state.futureForecasts" :key="`future-inventory-${index}`">
                <TableCell
                  :tool-tip-value="property.endOfMonthInventory.addComma()"
                  :font-size="'fontSizeS'"
                  :is-border="false"
                  class="forecast-history__table--cell-value"
                >
                  {{ property.endOfMonthInventory.addComma() }}
                </TableCell>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- 在庫推移予測部分の領域 -->
    <div class="order-form__simulation-graph-header">
      <div :data-testid="`forecast_label`" class="order-form__table-title">
        {{ t('在庫推移予測(詳細ページ)') }}
      </div>
      <div class="order-form__simulation-graph-header--right">
        <button
          v-if="state.isAppearenceUnpaidOrderedOrder"
          class="order-form__unpaid-ordered-order-notify"
          @click="() => recommendOrderStore.openUnpaidOrderedOrderInfoModal()"
        >
          {{ t('発注済みで未納になっている発注があります') }}
        </button>
        <TextButton
          class="order-form-simulation-graph-button-container__output-button"
          :class="recommendOrderStore?.state.errorType ? 'order-form-simulation-graph-button-container__output-button--inactive' : ''"
          :button-style="'btn-cancel'"
          :size="'btn-md'"
          :tool-tip-value="''"
          @click="exportChartDataCsv()"
        >
          {{ t('グラフデータ出力(詳細ページ)') }}
          <SvgLoader
            class="order-form-simulation-graph-button-container__output-button--arrow-icon"
            :name="'fatArrowDown'"
            data-testid="fat_arrow_down_icon"
          />
        </TextButton>
      </div>
    </div>
    <!-- <div class="offered-announce">
      <div class="text-center">
        <div :data-testid="'forecast_undo_redo'" class="offered-announce__amount-condition-control place-items-center">
          <IconButton
            class="offered-announce__amount-condition-control--undo-redo-button"
            :icon-name="'leftArrow'"
            data-testid="`offered_announce_undo_button`"
            :tool-tip-value="'計算結果を前に戻す'"
            :button-style="'btn-primary'"
            :disabled="!canUndoForecastStack()"
            :on-click="undoForecastStack"
          />
          <IconButton
            class="offered-announce__amount-condition-control--undo-redo-button"
            :icon-name="'rightArrow'"
            :data-testid="`offered-announce_undo_button`"
            :tool-tip-value="'計算結果を次に進める'"
            :button-style="'btn-primary'"
            :disabled="!canRedoForecastStack()"
            :on-click="redoForecastStack"
          />
        </div>
        <TextButton
          :data-testid="`forecast_reset_button`"
          class="offered-announce__amount-condition-control--forecast-reset-button"
          :tool-tip-value="'内示数量を初期値に戻す'"
          :color="'default'"
          :on-click="resetForecastCalculationResults"
        >
          {{ t('内示初期化') }}
        </TextButton>
      </div>
      <div class="offered-announce__table table-border__style overflow-hidden">
        <table class="offered-announce__table--style">
          <thead class="bg-gray-200">
            <tr>
              <TableHead
                v-for="(property, index) of state.forecastDates"
                :key="`second-${index}`"
                :data-testid="`forecast-child`"
                :text-align="'left'"
                class="px-2 py-1 font-semibold text-gray-900"
              >
                {{ t('displayShortDate', { year: property.year, month: convertMonthWithZero(property.month) }) }}
              </TableHead>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 border-t border-gray-100" style="background: white">
            <tr>
              <template v-for="(property, index) of state.forecastDates" :key="`second-${index}`">
                <TableCell :tool-tip-value="property.amount" class="px-2 py-1 font-normal text-gray-900" :text-align="'left'">
                  <NumberField
                    style="width: 80%"
                    :label-style="styles.amountLabel"
                    :label-square="false"
                    :form="state.formSet"
                    data-type="value"
                    :input-value="property.amount"
                    @change="(value) => forecastChange(value, index)"
                  />
                </TableCell>
              </template>
            </tr>
          </tbody>
        </table>
        <div class="calculate-forecast-rate">
          <div class="calculate-forecast-rate__data-input">
            <div class="calculate-forecast-rate__data-input--title-label">{{ t('内示計算') }}</div>
            <div class="flex">
              <SimpleSelectField
                class="calculate-forecast-rate__data-input--rate-selector"
                :data-testid="`rate_selector`"
                :selected-value="state.selectedCalculationUnit"
                :options="state.calculationUnit"
                :is-border="true"
                @update:model-value="updateOperator($event)"
              />
              <NumberField
                style="width: 64px"
                :label-style="styles.amountLabel"
                :input-style="styles.rateInput"
                :label-square="false"
                :form="state.formSet"
                data-type="value"
                :input-value="state.rate"
                @change="rateChange"
              />
            </div>
          </div>
          <div class="calculate-forecast-rate__calculate-button">
            <TextButton
              class="calculate-forecast-rate__calculate-button--style"
              :tool-tip-value="'内示数量を任意の倍率で計算する'"
              :color="'primary'"
              :on-click="adaptRate"
            >
              {{ t('計算する') }}
            </TextButton>
          </div>
        </div>
      </div>
      <TextButton
        class="offered-announce__simulate-start-button"
        :data-testid="`simulate-button`"
        :tool-tip-value="'シミュレーショングラフに反映する'"
        :color="'primary'"
        :on-click="adaptRate"
      >
        {{ t('在庫') }} <br />
        {{ t('シミュレーション') }}
      </TextButton>
    </div> -->
    <!-- 在庫シミュレーション部分の領域 -->
    <!-- <div class="simulate-result">
      <div :data-testid="`chart_label`" class="order-form__table-title">
        {{ t('在庫シミュレーション') }}
      </div>
      <SimpleSelectField
        class="simulate-result__selection"
        :data-testid="`rate_selector`"
        :selected-value="state.selectedDateUnit"
        :options="state.timeUnits"
        :is-border="true"
        @update:model-value="state.selectedDateUnit = $event as OrderChartDateUnit"
      />
    </div> -->
    <div class="simulate-graph__container">
      <Chart :date-unit="state.selectedDateUnit" :width="900" :height="400" :styles="orderChartStyles" :chart-data="state.chartData" />
      <div v-show="!(state.selectedDateUnit === 'month')" class="slidebar-multithumb">
        <!-- TODO 始点がずれると計算がおかしくなるので、一旦非表示にしておく -->
        <!-- <input id="range-0" v-model="state.inputGraphRange[0]" class="thumb-0" type="range" name="range-0" min="0" max="180" step="1" /> -->
        <input id="range-1" v-model="state.inputGraphRange[1]" class="thumb-1" type="range" name="range-1" max="180" step="1" min="0" />
        <span class="graph-range-left">{{ showGraphRange('left') }}</span>
        <span class="graph-range-right">{{ showGraphRange('right') }}</span>
      </div>
      <div v-if="recommendOrderStore?.state.errorType" class="simulate-graph__alert-container">
        <InvalidOrderAlert class="simulate-graph__alert-content" :error-type="recommendOrderStore.state.errorType" />
      </div>
    </div>
    <LeadTimePicker
      class="lead-time-picker"
      :popup-state="state.leadTimePickerState.shouldShow"
      :lead-time-set="state.leadTimeSet"
      @on-close="closeLeadTimePicker"
      @on-confirm="onConfirmLeadTimePicker"
    />
    <StdInventoryPicker
      class="std-inventory-picker"
      :popup-state="state.stdInventoryPickerState.shouldShow"
      :std-inventory-info="state.displayStdIndexInfo"
      @on-close="closeStdInventoryPicker"
      @on-confirm="onStdInventoryPicker"
    />

    <!-- 関連取引の販売実績・内示モーダル -->
    <RelatedTransactionsModal :business-id="props.item.businessId" />
  </div>
</template>

<style lang="scss" scoped>
.order-form-simulation-graph-button-container {
  display: grid;
  justify-content: space-between;
  grid-template-columns: auto auto;
  align-items: center;
  grid-gap: 10px;
  padding-bottom: 22px;

  @media (max-height: 1000px) {
    padding-bottom: 12px;
  }

  &__moq-info {
    &__text-button {
      color: $borderActive;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  &__order-package-info {
    &__text-button {
      color: $borderActive;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  &__memo-info {
    display: grid;
    align-items: center;
  }

  &__weight-info {
    width: auto;
    border-radius: 4px;
    background-color: $surfaceSecondary;
    padding: 8px 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 8px;
    font-family: Inter;
    font-weight: 400;
    font-size: $fontSizeS;
    color: $textDefault;
  }

  &__weight-info-row {
    display: grid;
    grid-template-columns: repeat(6, fit-content(100%));
    grid-gap: 24px;
    align-items: center;
    justify-content: start;
    padding: 0 24px;

    & + & {
      padding-top: 8px;
      border-top: 1px solid $borderSecondary;
    }
  }

  &__weight-info--responsible-users {
    overflow: hidden;
  }

  .accordion-toggle-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: grid;
    place-items: center;
    margin-left: auto;
  }

  .accordion-toggle-icon {
    transition: transform 0.3s ease;
    color: $textSecondary;
    &.is-expanded {
      transform: rotate(180deg);
    }
  }

  &__update-button {
    width: 186px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px, 24px;
    &--loop-icon {
      padding-left: 4px;
    }
  }
  &__output-button {
    display: flex;
    justify-content: end;
    align-items: center;
    padding: 10px, 16px;
    font-size: $fontSizeS;
    &--arrow-icon {
      padding-left: 4px;
    }
    &--inactive {
      opacity: $transparency50;
      pointer-events: none;
    }
  }
}
.order-form {
  height: 100%;
  overflow-y: scroll;
  padding: 8px;
  position: relative;

  &::-webkit-scrollbar {
    display: none; // chromeしか対応しない前提
  }

  &__simulation-graph-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    &--right {
      display: grid;
      grid-template-columns: auto auto;
      align-items: center;
      gap: 4px;
    }
  }

  &__unpaid-ordered-order-notify {
    font-weight: 700;
    color: $semanticError;
    line-height: 1;
    border-bottom: 1px solid;
  }

  .page-top {
    display: grid;
    justify-content: space-between;
    grid-template-columns: auto auto;
    align-items: center;
    // 2アイテムで両端配置なのでgrid-gapは入れない

    /* 詳細画面に置いてあるすべてのテーブルタイトルのスタイル */
    &__table-title {
      margin-top: 0px;
      font-family: 'Noto Sans JP';
      text-align: start;
      font-size: 16px;
      line-height: 28px;
      font-weight: 700; /* bold */
      white-space: normal;
      font-family: Inter;
      color: $textDefault;
    }

    /* 推奨情報のページネーションアイテム */
    &__page-nation {
      display: flex;
      justify-content: center;
      align-items: center;
      grid-template-columns: repeat(3, fit-content(100%));
      grid-gap: 16px;
      margin-top: 8px;
      margin-bottom: 8px;

      &--arrow {
        display: grid;
        justify-content: center;
        align-items: center;
        grid-template-columns: repeat(2, fit-content(100%));
        grid-gap: 8px;

        &-text {
          font-size: $fontSizeS;
          font-family: 'Noto Sans JP';
          font-weight: 400;
          color: $textDefault;
        }
      }

      /* disabledの時のcss設定 */
      &--arrow[disabled] {
        opacity: $transparency50 !important;
        pointer-events: none !important;
      }

      &--current-page {
        font-size: $fontSizeM;
        font-family: 'Noto Sans JP';
        font-weight: 700;
        color: $textDefault;
      }
    }
  }

  .forecast-child {
    margin: 0 auto;
    width: 100%;
    height: 30px;
    width: 90%;
    margin: 2px;
    border: $border;
    border-width: 10px;
    background-color: $backgroundPrimary;
  }

  .business-transaction {
    &__table {
      padding-bottom: 12px;
    }
  }

  .show-forecast-history {
    position: relative;
    width: 100%;
    margin-bottom: 24px;
    &__separating-border {
      border-radius: 8px;
      border-width: 0.5px;
      border-color: lighten($border, 20%);
    }
    &__button {
      position: absolute;
      left: 0px;
      height: 32px;
      text-align: left;
      white-space: nowrap;
      margin-top: 4px;
    }
  }

  .forecast-history {
    margin-bottom: 24px;

    @media (max-height: 1000px) {
      margin-bottom: 12px;
    }

    &__table {
      border-collapse: collapse;
      background-color: $backgroundPrimary;
      font-size: 16px;
      color: $textDefault;
      width: 100%; // FIXME: テーブルフレームサイズに合わせるようにしたが、gridを使用するなら変わるかもしれない
      &--side-header {
        left: 0;
        z-index: 1;
        font-weight: 400;
        background-color: $semanticWhite;
        color: $textSecondary;
        text-align: left;
        padding: 0px 8px;
      }
      &--side-header-empty {
        left: 0;
        z-index: 1;
        font-weight: 400;
        background-color: $surfaceDefault;
      }
      &--cell-value {
        padding: 8px, 0px, 8px, 8px;
        min-height: 24px;
        color: $textSecondary;
      }
      &--cell-link {
        color: #4a90e2; // Light blue color like in the image
        text-decoration: underline;
        cursor: pointer;
        display: inline-block;
        &:hover {
          color: #357abd; // Darker blue on hover
        }
      }
    }
    &__label-container {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    &__label-inner-left {
      display: grid;
      grid-template-columns: auto auto;
      gap: 8px;
    }
    &__label-change-month {
      height: 30px;
      border-radius: 4px;
      padding: 0 8px;
      background-color: white;
      &-input {
        height: 100%;
      }
    }

    &__label-percentage {
      background-color: $semanticWhite;
      padding: 0px, 24px, 0px, 24px;
      border-radius: 4px;
      height: 30px;
      min-width: 191px;
      display: flex;
      justify-content: center;
      align-items: center;

      &--tag {
        font-size: $fontSizeS !important;
        color: $textDefault;
      }

      &--number {
        color: $semanticPrimary;
        font-size: 26px !important;
        font-family: Roboto;
        font-weight: 700;
        padding: 2px 2px;
      }
      &--mark {
        font-size: $fontSizeS !important;
        color: $textDefault;
      }
    }
  }

  .table-border__style {
    border-radius: 8px;
    border-width: 0.5px;
    border-color: rgb(229 231 235); // TODO: テーブルに使用する枠線の配色を決める
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  }

  .offered-announce {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 7.5fr) minmax(0, 1.5fr);
    margin-right: 0px;
    margin-left: 0px;
    margin-bottom: 24px;
    width: 100%;
    height: auto;
    padding: 0 4px;
    &__amount-condition-control {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-top: 4px;
      margin-bottom: 4px;
      margin-right: auto;
      margin-left: auto;
      height: auto;
      width: 100%;
      &--undo-redo-button {
        width: 32px;
        height: 32px;
        padding: 4px;
      }
      &--forecast-reset-button {
        width: 90%;
        max-width: 88px;
        min-width: 80px;
        display: inline-block;
      }
    }
    &__table {
      display: grid;
      grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
      margin: 0px auto;
      width: 98%;
    }
    &__table--style {
      margin-right: auto;
      margin-left: auto;
      border-radius: 8px;
      border-width: 1px;
      border-collapse: collapse;
      background-color: $backgroundPrimary;
      text-align: left;
      font-size: 16px;
      color: $semanticError;
    }
    &__simulate-start-button {
      height: 48px;
      margin-top: auto;
      margin-bottom: auto;
    }

    .calculate-forecast-rate {
      display: flex;
      margin: 0 auto;
      &__data-input {
        margin-top: auto;
        margin-bottom: 8px;
        margin-right: 8px;
      }
      &__data-input--rate-selector {
        margin-right: 4px;
      }
      &__calculate-button {
        margin-top: auto;
        margin-bottom: 8px;
        max-width: 96px;
        min-width: 48px;
      }
      &__calculate-button--style {
        width: 100%;
      }
    }
  }

  .simulate-result {
    text-align: center;
    display: flex;
    position: relative;
    &__selection {
      width: 20%;
      margin-bottom: -8px;
      position: absolute;
      z-index: 1;
      text-align: right;
      right: 0px;
      bottom: 0px;
    }
  }

  .simulate-graph {
    &__container {
      position: relative;
    }

    &__alert-container {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      background-color: $modalScrim;
    }

    &__alert-content {
      position: absolute;
      top: 30px;
      left: 100px;
      right: 100px;
      bottom: 30px;
      z-index: 2;
    }
  }

  .slidebar-multithumb {
    margin: auto;
    display: grid;
    padding: 1rem;
    background-color: $semanticWhite;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  .slidebar-multithumb input[type='range'] {
    grid-column: 1;
    grid-row: 2;
    pointer-events: none;
    height: 15px;
    /* レンジバーの背景を透明にする */
    -webkit-appearance: none;
    background: none;
    /* overflow: hiddenにすると溝に色を付けたとき、その部分が巨大化しない */
    overflow: hidden;
  }

  .slidebar-multithumb ::-webkit-slider-thumb {
    pointer-events: auto;
    /* つまみ部分を長方形にする */
    -webkit-appearance: none;
    width: 1rem;
    height: 1.5rem;
  }

  /* 最初のつまみを上にする(順番をわかりやすくするため) */
  .thumb-0 {
    z-index: 1;
  }

  /* つまみの溝の背景。thumb-0をnoneにすることで後ろのthumb-1が表示される */
  .thumb-0::-webkit-slider-runnable-track {
    background: none;
  }

  .thumb-1::-webkit-slider-runnable-track {
    background: $surfaceDefault;
  }

  /* それぞれのつまみの設定。box-shadowでつまみの左側を埋める色を設定できる。 */
  .thumb-0::-webkit-slider-thumb {
    height: 15px;
    background: $semanticPrimary;
  }

  .thumb-1::-webkit-slider-thumb {
    height: 15px;
    background: $semanticPrimary;
  }
  .graph-range-left {
    grid-column: 1;
    grid-row: 3;
  }
  .graph-range-right {
    grid-column: 1;
    grid-row: 3;
    text-align: right;
  }
}
.lead-time-picker {
  position: fixed;
  // クリックしたボタンの位置にピッカーを表示する
  top: v-bind('`${state.leadTimePickerState.originCoordinate.y}px`');
  left: v-bind('`${state.leadTimePickerState.originCoordinate.x}px`');
}
.std-inventory-picker {
  position: fixed;
  // クリックしたボタンの位置にピッカーを表示する
  top: v-bind('`${state.stdInventoryPickerState.originCoordinate.y}px`');
  left: v-bind('`${state.stdInventoryPickerState.originCoordinate.x}px`');
}
.moq-input-popup {
  position: absolute;
  top: v-bind('`${state.moqInputPopUpState.originCoordinate.y}px`');
  left: v-bind('`${state.moqInputPopUpState.originCoordinate.x}px`');
}
.order-package-input-popup {
  position: absolute;
  top: v-bind('`${state.orderPackageInputPopUpState.originCoordinate.y}px`');
  left: v-bind('`${state.orderPackageInputPopUpState.originCoordinate.x}px`');
}
</style>
